import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { FastField, Field, Formik } from 'formik';
import KTImageInput from 'general/components/OtherKeenComponents/FileUpload/KTImageInput';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTFormTextArea from 'general/components/OtherKeenComponents/Forms/KTFormTextArea';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';

DialogAddVehicleModelView.propTypes = {
  item: PropTypes.object,
  chargeVehicleBrandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

function DialogAddVehicleModelView({ item, chargeVehicleBrandId, onCancel, onDone }) {
  // MARK: ---- PROPERTIES ----
  const editMode = !!item;
  const [imageSrc, setImageSrc] = useState(null);
  const refFormik = useRef(null);
  const initialFormikValues = useMemo(() => {
    return {
      brandId: chargeVehicleBrandId,
      name: item?.name || '',
      description: item?.description || '',
      image: item?.image || '',
      batteryCapacity: item?.batteryCapacity || 0,
      connectorType: item?.connectorType || '',
      vehicleType: item?.vehicleType || ChargeConstants.VehicleType.CAR,
    };
  }, [item, chargeVehicleBrandId]);
  const [loading, setLoading] = useState(false);

  // MARK: ---- METHODS ----
  const handleSubmit = async (values) => {
    setLoading(true);
    let param = { ...values };
    delete param.image;
    if (imageSrc) {
      const res = await miscHelper.callApiUploadFile(imageSrc);
      if (_.isArray(res) && res.length > 0) {
        param.image = res[0].relativePath;
      }
    }
    const iBatteryCapacity = values?.batteryCapacity
      ? _.toNumber(_.toString(values?.batteryCapacity)?.replace(',', '.'))
      : 0;
    param.batteryCapacity = iBatteryCapacity;
    param = _.omitBy(param, _.isNil);
    try {
      if (!editMode) {
        const response = await ChargeHelper.API.ChargeVehicleModel.requestAddChargeVehicleModel(
          chargeVehicleBrandId,
          param
        );
        if (_.isBoolean(response) && response) {
          ToastHelper.showSuccess('Thêm dòng xe thành công');
          if (onDone) {
            onDone();
          }
        } else if (_.isString(response)) {
          ToastHelper.showError(response);
          return;
        }
      } else {
        const response = await ChargeHelper.API.ChargeVehicleModel.requestUpdateChargeVehicleModel(
          chargeVehicleBrandId,
          item?.modelId,
          param
        );
        if (_.isBoolean(response) && response) {
          ToastHelper.showSuccess('Cập nhật dòng xe thành công');
          if (onDone) {
            onDone();
          }
        } else if (_.isString(response)) {
          ToastHelper.showError(response);
          return;
        }
      }
    } catch (error) { }
    setLoading(false);
  };

  return (
    <div>
      <Formik
        initialValues={initialFormikValues}
        enableReinitialize
        onSubmit={(values) => {
          handleSubmit(values);
        }}
        validationSchema={ChargeHelper.ValidationSchema.chargeVehicleModel}
      >
        {(formikProps) => {
          refFormik.current = formikProps;
          return (
            <div className="d-flex flex-column">
              <div className="row m-0 p-0 px-3 mt-4">
                <div className="col-12 mb-4 px-2">
                  <div className="w-120px">
                    {UIHelper.buildFormLabel('Logo', true)}
                    <FastField name="image">
                      {({ field, form, meta }) => (
                        <KTImageInput
                          fullWidth={true}
                          aspectRatio={'1'}
                          name={field.name}
                          editable={true}
                          value={Utils.getFullUrl(field.value)}
                          acceptImageTypes={['image/png', 'image/jpg', 'image/jpeg']}
                          defaultImage={AppResource.images.chargeConnectorTypes.vehicleDefault}
                          enableCheckValid
                          onSelectedFile={(file) => {
                            setImageSrc(file);
                            const imageUrl = URL.createObjectURL(file);
                            form.setFieldValue(field.name, imageUrl);
                          }}
                          onRemovedFile={() => {
                            setImageSrc(null);
                            form.setFieldValue(field.name, '');
                          }}
                          isValid={_.isEmpty(meta.error)}
                          isTouched={meta.touched}
                          feedbackText={meta.error}
                        />
                      )}
                    </FastField>
                  </div>
                  {UIHelper.buildFormText('Định dạng: JPG, PNG, JPEG')}
                </div>
              </div>
              <div className="row m-0 p-0 px-3">
                <div className="col-6 p-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildFormLabel('Tên dòng xe', true)}
                    <FastField name="name">
                      {({ field, form, meta }) => (
                        <KTFormInput
                          type={KTFormInputType.text}
                          placeholder="Nhập tên dòng xe"
                          name={field.name}
                          value={field.value}
                          onChange={(value) => {
                            form.setFieldValue(field.name, value);
                          }}
                          onBlur={() => {
                            form.setFieldTouched(field.name, true);
                          }}
                          enableCheckValid={true}
                          isValid={_.isEmpty(meta.error)}
                          isTouched={meta.touched}
                          feedbackText={meta.error}
                        />
                      )}
                    </FastField>
                  </div>
                </div>
                <div className="col-6 p-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildFormLabel('Dung lượng pin', true)}
                    <FastField name="batteryCapacity">
                      {({ field, form, meta }) => (
                        <KTFormInput
                          type={KTFormInputType.currency}
                          unit="kWh"
                          placeholder="Nhập dung lượng pin"
                          name={field.name}
                          value={field.value}
                          onChange={(value) => {
                            form.setFieldValue(field.name, value);
                          }}
                          onBlur={() => {
                            form.setFieldTouched(field.name, true);
                          }}
                          enableCheckValid={true}
                          isValid={_.isEmpty(meta.error)}
                          isTouched={true}
                          feedbackText={meta.error}
                        />
                      )}
                    </FastField>
                  </div>
                </div>
                <div className="col-6 p-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildFormLabel('Loại vòi sạc')}
                    <FastField name="connectorType">
                      {({ field, form, meta }) => (
                        <KTBSDropdown
                          name={field.name}
                          toggleElement={
                            <button
                              className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                              type="button"
                            >
                              {field.value?.length ? ChargeHelper.Utils.getChargeConnectorTypeName(field.value) : 'Chọn loại cổng sạc'}
                            </button>
                          }
                          selectedValue={field.value}
                          dropdownMenuItems={ChargeHelper.Data.chargeConnectorTypeOptions}
                          dropdownMenuClassName="overflow-auto w-100"
                          dropdownMenuStyle={{ maxHeight: '30vh' }}
                          onChange={(value) => {
                            form.setFieldValue(field.name, value);
                          }}
                          isDropdownForm={true}
                        />
                      )}
                    </FastField>
                  </div>
                </div>
                <div className="col-6 p-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildFormLabel('Loại xe', true)}
                    <FastField name="vehicleType">
                      {({ field, form, meta }) => (
                        <KTBSDropdown
                          name="vehicleType"
                          toggleElement={
                            <button
                              className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                              type="button"
                            >
                              {_.find(
                                ChargeHelper.Data.vehicleTypeOptions,
                                (item) => item.value === field.value
                              )?.name ?? 'Chọn loại xe'}
                            </button>
                          }
                          selectedValue={field.value}
                          dropdownMenuItems={ChargeHelper.Data.vehicleTypeOptions}
                          dropdownMenuClassName="overflow-auto w-100"
                          dropdownMenuStyle={{ maxHeight: '30vh' }}
                          onChange={(value) => {
                            form.setFieldValue(field.name, value);
                          }}
                          isDropdownForm={true}
                        />
                      )}
                    </FastField>
                  </div>
                </div>
                <div className="col-12 p-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildFormLabel('Mô tả')}
                    <FastField name="description">
                      {({ field, form, meta }) => (
                        <KTFormTextArea
                          placeholder="Nhập mô tả"
                          rows={3}
                          name={field.name}
                          value={field.value}
                          onChange={(value) => {
                            form.setFieldValue(field.name, value);
                          }}
                          onBlur={() => {
                            form.setFieldTouched(field.name, true);
                          }}
                          enableCheckValid={true}
                          isValid={_.isEmpty(meta.error)}
                          isTouched={meta.touched}
                          feedbackText={meta.error}
                        />
                      )}
                    </FastField>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>

      {/* footer */}
      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
        {UIHelper.buildGreenButton(
          !editMode ? 'Thêm' : 'Cập nhật',
          () => {
            refFormik.current.handleSubmit();
          },
          null,
          false,
          loading
        )}
      </div>
    </div>
  );
}

export default DialogAddVehicleModelView;
